// hooks/auth/useAuthFB.ts
import { useEffect, useState } from "react";
import { Platform } from "react-native";
import * as AuthSession from "expo-auth-session";
import * as WebBrowser from "expo-web-browser";
import * as SecureStore from "expo-secure-store";
import {
  makeRedirectUri,
  useAuthRequest,
  ResponseType,
} from "expo-auth-session";
import {
  GoogleAuthProvider,
  signInWithCredential,
  User,
} from "firebase/auth";
import { auth } from "../../firebase";
import { useAuth } from "../../context/Auth/AuthContext";
import { getUserIdByEmail, setBearerToken } from "../../services/TimeTracking/api";
import { loginWithBackend } from "../../services/Auth/authService";

WebBrowser.maybeCompleteAuthSession();

const isWeb = Platform.OS === "web";
const CLIENT_ID = process.env.GOOGLE_CLIENT_ID!;
const GAIA_CLIENT_ID = process.env.GAIA_CLIENT_ID!;
const GAIA_BACKEND = process.env.GAIA_BACKEND!;

const redirectUri = makeRedirectUri({ useProxy: true } as AuthSession.AuthSessionRedirectUriOptions)

let  TOKEN_KEY = "auth_token";
const USER_KEY = "user_info";

const storeItem = async (key: string, value: string) => {
  if (isWeb) {
    localStorage.setItem(key, value);
  } else {
    await SecureStore.setItemAsync(key, value);
  }
};

const getItem = async (key: string): Promise<string | null> => {
  return isWeb
    ? localStorage.getItem(key)
    : await SecureStore.getItemAsync(key);
};

const deleteItem = async (key: string) => {
  if (isWeb) {
    localStorage.removeItem(key);
  } else {
    await SecureStore.deleteItemAsync(key);
  }
};

export const useAuthFB = () => {
  const { setUserInfo } = useAuth();
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const [request, response, promptAsync] = AuthSession.useAuthRequest(
    {
        clientId: CLIENT_ID,
        responseType: AuthSession.ResponseType.IdToken,
        scopes: ["openid", "profile", "email"],
        redirectUri: makeRedirectUri({ useProxy: true } as AuthSession.AuthSessionRedirectUriOptions),
        extraParams: {
            nonce: "random-nonce", // Firebase requires nonce
        },
        usePKCE: false, // ✅ THIS FIXES THE ERROR
    },
    {
      authorizationEndpoint: process.env.GOOGLE_AUTH_ENDPOINT!
    }
  );

  // Restore session
  useEffect(() => {
    const restoreSession = async () => {
      const token = await getItem(TOKEN_KEY);
      const userData = await getItem(USER_KEY);
      if (token && userData) {
        setAccessToken(token);
        setBearerToken(token);
        setUserInfo(JSON.parse(userData));
      }
    };

    restoreSession();
  }, []);

  // Handle response from Google Sign-In
  useEffect(() => {
    const handleGoogleResponse = async () => {
      if (response?.type === "success") {
        try {
          const id_token = response.params.id_token;
          const credential = GoogleAuthProvider.credential(id_token);
          const firebaseUserCredential = await signInWithCredential(auth, credential);

          const user = firebaseUserCredential.user;

          const backendPayload = {
            username: user.email!,
            password: user.uid,
            grant_type: "password",
            client_id: CLIENT_ID,
            scope: "*",
          };

          
          const result = await loginWithBackend(backendPayload);
           if (result.access_token) {
            await storeItem(TOKEN_KEY, result.access_token);
            setBearerToken(result.access_token);
            setAccessToken(result.access_token);

            const userInfoFromFirebase = {
              name: user.displayName,
              email: user.email,
              picture: user.photoURL,
            };

            if (user.email) {
              const appUser = await getUserIdByEmail(user.email);
              const userWithAppId: any = {
                ...userInfoFromFirebase,
                appUserId: appUser?.id,
                given_name: appUser?.firstName,
                family_name: appUser?.lastName,
                photo: appUser?.photo,
                manager: appUser?.manager,
              };

              setUserInfo(userWithAppId);
              await storeItem(USER_KEY, JSON.stringify(userWithAppId));
            }

            console.log("✅ Welcome!");
          }
        } catch (error) {
          console.error("❌ Firebase login failed:", error);
        }
      }
    };

    handleGoogleResponse();
  }, [response]);

  const loginWithGoogle = async () => {
    const result = await promptAsync();
    if (result.type !== "success") {
      console.warn("❌ Google login failed or cancelled");
      return null;
    }
    // useEffect handles success
  };

  const logout = async () => {
    setAccessToken(null);
    setUserInfo(null)
    setUserInfo(null);
    await deleteItem(TOKEN_KEY);
    await deleteItem(USER_KEY);
    await auth.signOut();
  };

  const isTokenExpired = async (): Promise<boolean> => {
    const token = await getItem(TOKEN_KEY);
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp < now;
    } catch (err) {
      console.warn("❌ Error parsing token:", err);
      return true;
    }
  };

  return {
    loginWithGoogle,
    logout,
    accessToken,
    isTokenExpired,
  };
};
